// Messaging state — ports the message/queue/loading fields that lived on the
// old ChatApp instance (messaging.js + connection.js). Messages persist to
// localStorage (as the old saveMessages did) so a reload shows them instantly
// before /history refreshes.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createPersistentStorage } from '../lib/persistentStorage'
import * as metrica from '../lib/metrica'
import { formatResetIn } from '../lib/limitNotice'
import { useProfileStore } from './profileStore'
import type { ChatMessage } from '../api/chat'
import type { MessageMetadata } from '../types/api'

const DEFAULT_PAYWALL_INTRO = 'Пробный период завершён. Выберите тариф, чтобы продолжить:'

// Shown when a one-time token pack ("Сессия", plan_type=token_based) is depleted:
// the balance never resets, so this upsells another pack / a subscription instead
// of the weekly-reset "wait N days" notice used for recurring plans.
const SESSION_DEPLETED_TEXT = `На вашем счету закончились доступные сообщения — мы подошли к паузе.
Вы уже сделали важный шаг в работе над собой — и можете продолжить, когда будете готовы:
Ещё один пакет «Сессия» — пополним ваш баланс на еще столько же общения, без ограничения по времени.
Тариф «Месяц» — если планируете работать регулярно, он даёт заметно больше пространства для глубокой работы.
Продолжим, как только вам будет удобно`

interface AddMessageOptions {
  contentType?: string
  mediaUrl?: string | null
  metadata?: MessageMetadata | null
}

interface MessagingState {
  messages: ChatMessage[]
  isLoading: boolean
  typing: boolean
  pendingMessages: string[]
  setMessages: (messages: ChatMessage[]) => void
  addMessage: (role: 'user' | 'assistant', content: string, options?: AddMessageOptions) => void
  clear: () => void
  clearPaywall: () => void
  addPaywall: (content?: string) => void
  addLimitNotice: (resetAt: string | null | undefined) => void
  setLoading: (isLoading: boolean) => void
  setTyping: (typing: boolean) => void
  setRating: (index: number, emoji: string) => void
  queuePending: (content: string) => void
  takePending: () => string | undefined
  unshiftPending: (content: string) => void
}

export const useMessagingStore = create<MessagingState>()(
  persist(
    (set, get) => ({
      messages: [],
      isLoading: false,
      typing: false,
      pendingMessages: [], // offline outbox (not persisted across reloads is fine)

      // The paywall bubble is client-only — the backend never persists its
      // trigger as a normal chat row (http_message_history has no metadata
      // column for it), so a plain /history refetch can never include it.
      // Preserve a trailing local paywall bubble across the replace instead
      // of letting it silently disappear (parent bd-…: paywall vanished after
      // navigating to /pricing and back, racing the WS reconnect's re-push).
      setMessages: (messages) =>
        set((s) => {
          const lastOld = s.messages[s.messages.length - 1]
          const lastNew = messages[messages.length - 1]
          if (lastOld?.contentType === 'paywall' && lastNew?.contentType !== 'paywall') {
            return { messages: [...messages, lastOld] }
          }
          return { messages }
        }),

      addMessage: (role, content, { contentType = 'text', mediaUrl = null, metadata = null } = {}) =>
        set((s) => ({
          messages: [
            ...s.messages,
            { role, content, contentType, mediaUrl, metadata, timestamp: Date.now(), reactions: [] },
          ],
        })),

      clear: () => set({ messages: [] }),

      // Payment succeeded → the trailing local paywall bubble (preserved by
      // setMessages across history refetches, see above) is now stale and
      // would otherwise stick around forever, since nothing else ever clears
      // it. Drop it once access is restored.
      clearPaywall: () =>
        set((s) => {
          const last = s.messages[s.messages.length - 1]
          if (last?.contentType !== 'paywall') return {}
          return { messages: s.messages.slice(0, -1) }
        }),

      // Inline paywall (parent feat/inline-paywall): access-denied renders a
      // paywall bubble in the transcript instead of switching to /pricing.
      // At most ONE paywall bubble is ever shown: a new denial (proactive
      // mount re-check, a WS show_pricing, or a 402) replaces the most recent
      // paywall bubble instead of stacking a second one. Stacking produced two
      // sets of plan buttons (duplicate "Месяц" → strict-mode/UX bug) when the
      // proactive mount check and the reactive 402 both fired for the same
      // depleted token-pack user, separated by the user's message.
      addPaywall: (content = DEFAULT_PAYWALL_INTRO) =>
        set((s) => {
          // Find the most recent paywall bubble (not just the tail): the user's
          // message may sit between the proactive bubble and this one.
          const lastPaywallIdx = s.messages
            .map((m) => m.contentType)
            .lastIndexOf('paywall')
          // Same content already at that slot → nothing changed, don't re-log.
          if (lastPaywallIdx !== -1 && s.messages[lastPaywallIdx].content === content) return {}
          metrica.reachGoal('paywall_inline_shown')
          const base =
            lastPaywallIdx !== -1
              ? s.messages.slice(0, lastPaywallIdx)
              : s.messages
          return {
            messages: [
              ...base,
              {
                role: 'assistant',
                content,
                contentType: 'paywall',
                mediaUrl: null,
                timestamp: Date.now(),
                reactions: [],
              },
            ],
          }
        }),

      // Quota-reached handling for SUBSCRIBED users (reason='subscription_limit').
      // A recurring subscription's weekly limit really does reset → show the
      // "wait N days" notice. A one-time token pack ("Сессия") never resets →
      // upsell with the inline paywall (buttons) + tailored copy instead.
      addLimitNotice: (resetAt) => {
        if (useProfileStore.getState().stats?.plan_type === 'token_based') {
          metrica.reachGoal('session_depleted_shown')
          get().addPaywall(SESSION_DEPLETED_TEXT)
          return
        }
        set((s) => {
          metrica.reachGoal('limit_notice_shown')
          const text = `Вы использовали недельный лимит сообщений по вашей подписке. ${formatResetIn(resetAt)}`
          return {
            messages: [
              ...s.messages,
              { role: 'assistant', content: text, contentType: 'text', mediaUrl: null, timestamp: Date.now(), reactions: [] },
            ],
          }
        })
      },

      setLoading: (isLoading) => set({ isLoading }),
      setTyping: (typing) => set({ typing }),

      // Local-only 👍/👎 rating. Exclusive: picking one replaces the other, and
      // clicking the active one clears it. (The old reactions.js allowed up to 3
      // emoji at once, but a two-option rating should be single-choice.)
      setRating: (index, emoji) =>
        set((s) => {
          const messages = s.messages.slice()
          const msg = messages[index]
          if (!msg || msg.role !== 'assistant') return {}
          const current = msg.reactions || []
          messages[index] = { ...msg, reactions: current.includes(emoji) ? [] : [emoji] }
          return { messages }
        }),

      queuePending: (content) => set((s) => ({ pendingMessages: [...s.pendingMessages, content] })),
      takePending: () => {
        const [head, ...rest] = get().pendingMessages
        set({ pendingMessages: rest })
        return head
      },
      unshiftPending: (content) => set((s) => ({ pendingMessages: [content, ...s.pendingMessages] })),
    }),
    {
      name: 'humanteq-messages',
      storage: createPersistentStorage(),
      // Only persist the visible transcript, not transient flags / outbox.
      partialize: (s) => ({ messages: s.messages }),
    }
  )
)
